import { useState, useCallback, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

// ── Generic Picker types ────────────────────────────────────────

export interface PickerItem {
  readonly id: string
  readonly label: string
  readonly description?: string
}

interface PickerProps<T extends PickerItem> {
  readonly isOpen: boolean
  readonly onClose: () => void
  readonly onSelect: (item: T) => void
  readonly items: readonly T[]
  readonly title: string
  readonly searchPlaceholder?: string
  readonly selectedId?: string
  readonly isLoading?: boolean
  readonly renderItem?: (item: T, isSelected: boolean) => React.ReactNode
}

// ── Generic Picker component ────────────────────────────────────

export function Picker<T extends PickerItem>({
  isOpen,
  onClose,
  onSelect,
  items,
  title,
  searchPlaceholder,
  selectedId,
  isLoading = false,
  renderItem,
}: PickerProps<T>) {
  const { t } = useTranslation()
  const [query, setQuery] = useState('')
  const inputRef = useRef<HTMLInputElement>(null)

  // Reset search when opened
  useEffect(() => {
    if (isOpen) {
      setQuery('')
      // Focus search input after animation
      const timer = setTimeout(() => { inputRef.current?.focus() }, 100)
      return () => { clearTimeout(timer) }
    }
  }, [isOpen])

  // Block body scroll when open
  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden'
      return () => { document.body.style.overflow = '' }
    }
  }, [isOpen])

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }
    document.addEventListener('keydown', handler)
    return () => { document.removeEventListener('keydown', handler) }
  }, [isOpen, onClose])

  const filtered = useMemo(() => {
    if (!query.trim()) return items
    const lower = query.toLowerCase()
    return items.filter(
      (item) =>
        item.label.toLowerCase().includes(lower) ||
        (item.description?.toLowerCase().includes(lower) ?? false),
    )
  }, [items, query])

  const handleSelect = useCallback(
    (item: T) => {
      onSelect(item)
      onClose()
    },
    [onSelect, onClose],
  )

  if (!isOpen) return null

  return (
    <div
      className="fixed inset-0 z-[100] flex flex-col bg-tg-bg animate-slide-up"
    >
      {/* Header */}
      <div
        className="flex shrink-0 items-center border-b border-tg-hint/20 px-4 py-3 bg-tg-secondary shadow-sm"
      >
        <button
          type="button"
          onClick={onClose}
          className="mr-3 rounded-lg px-2 py-1 text-sm transition-all duration-150 active:opacity-70 active:scale-95 text-tg-button"
        >
          ← {t('common.back')}
        </button>
        <h2
          className="flex-1 text-center text-base font-semibold text-tg-text"
        >
          {title}
        </h2>
        <div className="w-16" /> {/* Spacer to center title */}
      </div>

      {/* Search */}
      <div className="shrink-0 px-4 py-3">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => { setQuery(e.target.value) }}
          placeholder={searchPlaceholder ?? t('common.searchPlaceholder')}
          className="w-full rounded-xl border border-tg-hint/20 px-3 py-2.5 text-sm outline-none transition-all duration-150 focus:border-[var(--tg-theme-button-color)] focus:shadow-sm bg-tg-secondary text-tg-text hover:border-tg-hint/40"
        />
      </div>

      {/* Item list */}
      <div className="flex-1 overflow-y-auto px-4 pb-4">
        {isLoading ? (
          <div
            className="py-8 text-center text-sm text-tg-hint"
          >
            {t('common.loading')}
          </div>
        ) : filtered.length === 0 ? (
          <div
            className="py-8 text-center text-sm text-tg-hint"
          >
            {query ? t('search.noResults') : '—'}
          </div>
        ) : (
          <div className="flex flex-col gap-1">
            {filtered.map((item) => {
              const isSelected = item.id === selectedId
              return renderItem ? (
                <div
                  key={item.id}
                  onClick={() => { handleSelect(item) }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if (e.key === 'Enter') handleSelect(item) }}
                >
                  {renderItem(item, isSelected)}
                </div>
              ) : (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => { handleSelect(item) }}
                  className="flex w-full flex-col rounded-lg px-3 py-2.5 text-left transition-all duration-150 active:opacity-70 active:scale-[0.99]"
                  style={{
                    backgroundColor: isSelected
                      ? 'color-mix(in srgb, var(--tg-theme-button-color) 15%, transparent)'
                      : 'var(--tg-theme-secondary-bg-color)',
                    borderLeft: isSelected ? '3px solid var(--tg-theme-button-color)' : '3px solid transparent',
                  }}
                >
                  <span
                    className="text-sm font-medium"
                    style={{
                      color: isSelected
                        ? 'var(--tg-theme-button-color)'
                        : 'var(--tg-theme-text-color)',
                    }}
                  >
                    {item.label}
                  </span>
                  {item.description && (
                    <span
                      className="mt-0.5 text-xs text-tg-hint"
                    >
                      {item.description}
                    </span>
                  )}
                </button>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}
